/**
 * Shared Glucera typography — Inter + design tokens from globals.css / tailwind.config.js.
 * Use these instead of arbitrary text-[Npx] or font-serif.
 */

import {
  measureActionLabel,
  measureCardHeading,
  measureCardMeta,
  measureCardValueSubhead,
  measureEyebrow,
  measureFocusTitle,
  measurePageSubtitle,
  measureSectionTitle,
  measureBody,
} from "./measurePageTypography";

export {
  measureEyebrow,
  measureEyebrowWarning,
  measureSectionTitle,
  measurePageSubtitle,
  measurePageTitle,
  measureCardTitle,
  measureCardHeading,
  measureCardValue,
  measureCardValueSubhead,
  measureCardMeta,
  measureCardMetaWarning,
  measureFocusTitle,
  measureFocusBody,
  measureBody,
  measureActionLabel,
  measurePrimaryButtonLabel,
} from "./measurePageTypography";

/** Page hero / empty-state headline (e.g. "You haven't logged today.") */
export const gluceraHeroTitle =
  "text-title1 font-semibold leading-tight tracking-tight text-foreground sm:text-large-title";

/** Standard page header title */
export const gluceraPageTitle = "text-2xl font-bold tracking-tight text-foreground";

/** Section header on sub-pages */
export const gluceraSectionTitle = "text-xl font-semibold text-foreground";

/** Uppercase micro label (badges, eyebrows) */
export const gluceraMicroLabel =
  "text-compact-xs font-bold uppercase tracking-[0.12em] text-muted-foreground";

/** Uppercase nano label */
export const gluceraNanoLabel =
  "text-2xs font-bold uppercase tracking-[0.1em] text-muted-foreground";

/** Body copy — alias measure screen body */
export const gluceraBody = measureBody;

/** Large metric number */
export const gluceraMetricLarge = "text-3xl font-bold tabular-nums text-foreground";

/** Card subheading */
export const gluceraCardHeading = "text-title2 font-semibold leading-snug text-foreground";

/** Button label on primary CTA */
export const gluceraButtonLabel = "text-sm font-semibold";

/** Meal analyze overlay — eyebrow badge */
export const gluceraLoaderEyebrow = measureEyebrow;

/** Meal analyze overlay — center headline (Inter semibold, same as focus cards) */
export const gluceraLoaderHeadline = measureFocusTitle;

/** Meal analyze overlay — step card title */
export const gluceraLoaderStepTitle = measureCardHeading;

/** Meal analyze overlay — step status (Complete / In progress) */
export const gluceraLoaderStepStatus = measureCardMeta;

/** Meal analyze overlay — percent badge */
export const gluceraLoaderPercentBadge =
  `${measureCardValueSubhead} tabular-nums text-background`;

/** Meal analyze overlay — progress footer row */
export const gluceraLoaderProgressMeta = measurePageSubtitle;

/** Review / scanned-food screen title */
export const gluceraReviewTitle = "text-title2 font-bold text-foreground";

/** Review screen caption */
export const gluceraReviewCaption = "text-caption1 text-muted-foreground";

/** Section heading (e.g. Food Items) */
export const gluceraSectionLabel = "text-title3 font-bold text-foreground";

/** Bottom sheet / modal title */
export const gluceraSheetTitle = "text-title2 font-semibold text-foreground";

/** Form field label */
export const gluceraFieldLabel = "text-callout font-medium text-foreground";

/** Form input & select text */
export const gluceraFieldInput =
  "text-callout text-foreground placeholder:text-muted-foreground";

/** Form control shell */
export const gluceraFormControl =
  "w-full rounded-lg border border-border bg-muted/40 px-4 py-3 text-callout text-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring";

/** Helper / hint under fields */
export const gluceraFieldHint = "text-subhead text-muted-foreground";

/** Numeric stepper value */
export const gluceraFormMetric = "text-title2 font-semibold tabular-nums text-foreground";

/** GHI / score circle number */
export const gluceraScoreDisplay = "text-large-title font-black tabular-nums";

/** Score card eyebrow */
export const gluceraScoreEyebrow =
  "text-caption2 font-semibold uppercase tracking-wider text-muted-foreground";

/** Primary save / submit CTA label */
export const gluceraPrimaryCta = "text-subhead font-bold";

/** Solid brand blue — primary CTAs without gradient */
export const gluceraBrandSolidBlueButton =
  "bg-[#737DFE] font-semibold text-white shadow-md shadow-[#737DFE]/30 transition-colors hover:bg-[#6268e8] disabled:opacity-50";

/** Logo-gradient shell — shared by assessment, auth, and primary CTAs */
export const gluceraBrandPrimaryButton =
  "bg-gradient-brand font-semibold text-primary-foreground shadow-brand transition-opacity hover:opacity-90 disabled:opacity-50";

/** Assessment / onboarding step — full-width Next button */
export const gluceraAssessmentCta =
  "h-11 w-full rounded-xl text-sm sm:h-12 sm:text-base " + gluceraBrandPrimaryButton;

/** Login + post-assessment screens — rounded-2xl CTA */
export const gluceraAuthCta =
  "h-11 w-full rounded-2xl text-sm sm:h-12 sm:text-base " + gluceraBrandPrimaryButton;

/** Welcome hero — Get Started */
export const gluceraWelcomeCta =
  "h-12 w-full rounded-2xl text-sm " + gluceraBrandPrimaryButton;

/** Sheet modal primary button */
export const gluceraSheetButtonPrimary = "text-callout font-semibold";

/** Sheet modal secondary button */
export const gluceraSheetButtonSecondary = "text-callout font-medium";

/** Small body / reason lines */
export const gluceraFootnoteBody = "text-footnote leading-relaxed text-muted-foreground";

/** Home page — readable eyebrows (Overall today, carb-fiber, etc.) */
export const gluceraHomeEyebrow =
  "text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground";

/** Home page — readable meta (3 meals, tap to manage) */
export const gluceraHomeMeta =
  "text-xs font-medium uppercase tracking-wide text-muted-foreground";

/** Home page — status badges (Poor, Lowest, Needs improvement) */
export const gluceraHomeStatusBadge =
  "text-compact-sm font-bold uppercase tracking-wide";

/** Home page — compact chips (Walk −18, streak) */
export const gluceraHomeChipBadge =
  "text-compact-sm font-bold uppercase tracking-wide";

/** Home / Meals — bottom-nav pages (same scale as Measure) ── */

export const gluceraPageSubtitle = measurePageSubtitle;

export const gluceraGreeting =
  "text-subhead font-semibold leading-snug text-muted-foreground";

export const gluceraDateCaption = "text-caption1 text-muted-foreground";

export const gluceraPageTitleResponsive =
  "text-xl font-bold leading-tight tracking-tight text-foreground sm:text-2xl";

export const gluceraListItem =
  "text-compact-sm font-semibold uppercase leading-snug tracking-wide";

export const gluceraWheelCaption =
  "whitespace-pre-line text-center text-compact-base font-semibold leading-tight text-zinc-600 dark:text-zinc-300";

export const gluceraStatusBadge =
  "text-compact-sm xs:text-xs font-bold uppercase tracking-wide";

export const gluceraStatNano =
  "text-2xs font-bold uppercase tracking-wide text-muted-foreground";

export const gluceraHintNano = "text-2xs leading-snug text-muted-foreground";

export const gluceraMetricHero =
  "text-3xl font-bold tracking-tight text-foreground sm:text-4xl";

export const gluceraMetricMd =
  "text-2xl font-bold tabular-nums tracking-tight text-foreground";

export const gluceraMacroLabel =
  "text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground";

export const gluceraMacroValue =
  "text-base font-bold tabular-nums tracking-tight text-foreground sm:text-lg";

export const gluceraRingPct =
  "text-compact-sm font-bold tabular-nums leading-none text-foreground";

export const gluceraOutlineButtonLabel = measureActionLabel;

export const gluceraMealCardTitle =
  "text-subhead font-bold leading-tight text-foreground";

export const gluceraSummaryValue = "text-lg font-black tabular-nums text-foreground";

export const gluceraUnitNano = "text-2xs font-semibold text-muted-foreground";

export const gluceraCalorieGoal =
  "pb-0.5 text-xs font-medium tabular-nums text-muted-foreground";

export const gluceraGoalMeta = "text-compact-xs font-medium tabular-nums text-muted-foreground";

/** Home live meal — selected activity chip (solid brand blue). */
export const gluceraHomeInterventionChipSelected =
  "bg-[#737DFE] text-white shadow-sm";

export const gluceraHomeInterventionChipIdle =
  "text-foreground/70 hover:bg-muted hover:text-foreground";

export const gluceraHomeInterventionStartButton =
  "rounded-xl active:scale-[0.98] " + gluceraBrandSolidBlueButton;

export const gluceraHomeInterventionStartSubtext =
  "font-semibold text-white/90";

export const gluceraHomeInterventionDropdownSelected =
  "bg-[#737DFE]/10 font-semibold text-[#737DFE] dark:bg-[#737DFE]/15 dark:text-[#737DFE]";

/* ── Trends page — same scale as Measure / Home / Profile ── */

export const gluceraTrendsSubtitle = measurePageSubtitle;

export const gluceraTrendsTitle = gluceraPageTitle;

export const gluceraTrendsSectionTitle = measureSectionTitle;

export const gluceraTrendsSectionBadge = gluceraMicroLabel;

export const gluceraTrendsCardLabel = gluceraMicroLabel;

export const gluceraTrendsCardLabelWide = gluceraMicroLabel;

export const gluceraTrendsMetricLg = gluceraMetricLarge;

export const gluceraTrendsMetricMd =
  "text-title2 font-bold tabular-nums text-foreground";

export const gluceraTrendsMetricUnit = "text-subhead font-semibold text-muted-foreground";

export const gluceraTrendsMetricMeta = "text-compact-sm font-medium text-muted-foreground";

export const gluceraTrendsStatLabel = measureCardMeta;

export const gluceraTrendsStatPct = "text-compact-xs font-medium text-muted-foreground";

/** Bar / line chart day labels (Mo–Su) */
export const gluceraTrendsChartDay = "text-compact-sm font-semibold text-muted-foreground";

export const gluceraTrendsSectionNum = "text-compact-sm font-bold text-white";

export const gluceraTrendsSubsectionTitle = measureSectionTitle;

export const gluceraTrendsCareHero = measureFocusTitle;

export const gluceraTrendsRowValue = measureCardValueSubhead;

export const gluceraTrendsRowUnit = "text-compact-sm font-medium text-muted-foreground";

export const gluceraTrendsVitalLabel = measureCardMeta;

export const gluceraTrendsInputLabel = measureActionLabel;

export const gluceraTrendsInputSub = "text-compact-sm font-medium text-muted-foreground";

export const gluceraTrendsTrendBadge = "text-compact-sm font-bold";
